import {
  calculateFocusSurfaceBox,
  calculateFocusWindowBox,
  focusWindowModeForPoint,
  type FocusWindowBox,
  type FocusWindowLayout,
  type FocusWindowMode,
} from "./focus-window-layout";

export const FOCUS_FRAME_DRAG_HEIGHT = 48;
// Expand the invisible target outward, preserving its 16px interior band.
export const FOCUS_FRAME_DRAG_OUTSET = 32;
// Frame title rows also support dragging below the top strip.
const MAX_RESTORED_FRAME_GRIP_OFFSET = 48;

/** Keep the top grab area reachable without blocking horizontal display seams. */
export function clampFrameHeaderToSurface(
  box: FocusWindowBox,
  surface: FocusWindowBox,
  headerHeight = FOCUS_FRAME_DRAG_HEIGHT,
): FocusWindowBox {
  const visibleHeight = Math.min(headerHeight, box.height, surface.height);
  return {
    ...box,
    y: Math.max(
      surface.y,
      Math.min(box.y, surface.y + surface.height - visibleHeight),
    ),
  };
}

export function isFrameHeaderDoubleClickTarget(target: EventTarget | null) {
  if (!(target instanceof Element)) return false;
  if (
    target.closest(
      "button, input, textarea, select, a, [data-no-drag], [role='button'], [role='tab']",
    )
  )
    return false;
  return Boolean(
    target.closest(
      "[data-frame-header='true'], [data-frame-drag-handle='true'], [data-detached-frame-drag-handle='true']",
    ),
  );
}

/** Keep the same proportional grip when a full-width frame restores. */
export function restoredFrameBoxForPointer(
  pointer: { x: number; y: number },
  box: FocusWindowBox,
  normalBox: FocusWindowBox,
): FocusWindowBox {
  const fraction = Math.min(1, Math.max(0, (pointer.x - box.x) / box.width));
  return {
    ...normalBox,
    x: Math.round(pointer.x - fraction * normalBox.width),
    y: Math.round(
      pointer.y -
        Math.min(
          MAX_RESTORED_FRAME_GRIP_OFFSET,
          Math.max(-FOCUS_FRAME_DRAG_OUTSET, pointer.y - box.y),
        ),
    ),
  };
}

export interface FocusSnapPreview {
  mode: Exclude<FocusWindowMode, "normal">;
  box: FocusWindowBox;
}

/** The snap a window dragged to `point` in a surface of `area` makes. */
export function focusSnapPreviewAt(
  point: { x: number; y: number },
  area: { width: number; height: number },
): FocusSnapPreview | null {
  const surface = calculateFocusSurfaceBox(area);
  const mode = focusWindowModeForPoint(point, surface);
  return mode ? { mode, box: calculateFocusWindowBox(mode, surface) } : null;
}

export function sameFocusSnapPreview(
  a: FocusSnapPreview | null,
  b: FocusSnapPreview | null,
): boolean {
  return (
    a?.mode === b?.mode &&
    a?.box.x === b?.box.x &&
    a?.box.y === b?.box.y &&
    a?.box.width === b?.box.width &&
    a?.box.height === b?.box.height
  );
}

/** A dragged window's normal box: a snapped one regains its normal size. */
export function draggedNormalBox(
  draggedBox: FocusWindowBox,
  focusWindow: FocusWindowLayout | null,
): FocusWindowBox {
  if (!focusWindow || focusWindow.mode === "normal") return draggedBox;
  return {
    x: draggedBox.x,
    y: draggedBox.y,
    width: focusWindow.normalBox.width,
    height: focusWindow.normalBox.height,
  };
}
